import React from 'react';
import { Link } from 'react-router-dom';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Star } from 'lucide-react';
import { MessageButton } from '@/components/chat/MessageButton';

interface SequenceCreatorCardProps {
  avatarUrl: string;
  creatorName: string;
  creatorTitle?: string;
  creatorId: string;
  profileUsername?: string;
  rating?: number;
  sequenceCount?: number;
  className?: string;
}

const SequenceCreatorCard: React.FC<SequenceCreatorCardProps> = ({
  avatarUrl,
  creatorName,
  creatorTitle = "Sequence Creator",
  creatorId,
  profileUsername,
  rating,
  sequenceCount,
  className
}) => {
  const profileUrl = profileUsername ? `/profile/${profileUsername}` : `/profile/${creatorId}`;

  return (
    <Card className={cn('group transition-all duration-200 hover:shadow-md', className)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Sequence Creator</CardTitle>
      </CardHeader>
      <CardContent>
        <Link to={profileUrl} className="block">
          <div className="flex items-center space-x-4">
            <Avatar className="transition-transform duration-200 group-hover:scale-105">
              <AvatarImage src={avatarUrl} alt={creatorName} />
              <AvatarFallback>{creatorName.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="font-medium group-hover:text-primary transition-colors">
                {creatorName}
              </div>
              <div className="text-sm text-muted-foreground">{creatorTitle}</div>
              {(rating !== undefined || sequenceCount !== undefined) && (
                <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                  {rating !== undefined && (
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4" />
                      <span>{rating.toFixed(1)}</span>
                    </div>
                  )}
                  {sequenceCount !== undefined && (
                    <div>
                      {sequenceCount} sequence{sequenceCount !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </Link>
        <div className="flex gap-2 mt-4">
          <Button
            asChild
            className="flex-1 opacity-80 group-hover:opacity-100 transition-opacity"
            variant="outline"
          >
            <Link to={profileUrl}>View Profile</Link>
          </Button>
          <MessageButton
            userId={creatorId}
            userName={creatorName}
            variant="outline"
            className="flex-1"
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default SequenceCreatorCard; 